import { trigger, transition, style, animate } from '@angular/animations';
import { Component, EventEmitter, Input, Output, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { OverlayComponent } from '@overlays/overlay/overlay.component';
import { DomainRole } from '@services/genesys-cloud-user.interface';
import { CongratsDialogComponent } from '@components/congrats-dialog/congrats-dialog.component';
import { BreakpointService } from '@services/breakpoint/breakpoint.service';
import { environment } from '@environments/environment';
import { firstValueFrom } from 'rxjs';
import { NgStyle } from '@angular/common';
import { CommunityListComponent } from '../../lists/community-list/community-list.component';
import { EducationCourseListComponent } from '../../lists/education-course-list/education-course-list.component';

@Component({
  selector: 'app-summary-overlay',
  templateUrl: './summary-overlay.component.html',
  styleUrls: ['./summary-overlay.component.scss'],
  animations: [
    trigger('fadeOut', [
      transition(':leave', [style({ opacity: 1 }), animate('500ms ease-out', style({ opacity: 0 }))]),
    ]),
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate(
          '600ms ease-out',
          style({
            opacity: 1,
          }),
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate(
          '500ms ease-out',
          style({
            opacity: 0,
          }),
        ),
      ]),
    ]),
    trigger('slideFromRightAndFadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(100%)' }), // Start from right
        animate(
          '600ms ease-out',
          style({
            opacity: 1,
            transform: 'translateX(0)',
          }),
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateX(0)' }),
        animate(
          '500ms ease-out',
          style({
            opacity: 1,
            transform: 'translateX(-100%)', // Exit to left
          }),
        ),
      ]),
    ]),
    trigger('slideFromLeftAndFadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-100%)' }), // Start from left
        animate(
          '600ms ease-out',
          style({
            opacity: 1,
            transform: 'translateX(0)', // Move to center
          }),
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateX(0)' }),
        animate(
          '500ms ease-out',
          style({
            opacity: 1,
            transform: 'translateX(100%)', // Exit to right
          }),
        ),
      ]),
    ]),
  ],
  imports: [NgStyle, CommunityListComponent, EducationCourseListComponent],
})
export class SummaryOverlayComponent extends OverlayComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private breakpointService = inject(BreakpointService);

  @Input() role: DomainRole;
  @Input() userName: string;
  @Output() readonly replay = new EventEmitter<void>();

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.showCongratsDialog();
  }
  /**
   * Emit replay event to replay video
   */
  replayVideo(): void {
    this.replay.emit();
  }

  /**
   * Update the overlay styles based on the viewport size
   */
  override updateOverlayStyles(): void {
    const { playerWidth = 0, playerHeight = 0, browserHeight = 0, browserWidth = 0 } = this.viewportSize || {};

    // Calculate dimensions and position
    let overlayWidth: number | string = playerWidth;
    let leftPosition: number | string = 0;
    let topPosition: number | string = 0;
    let calcMaxHeight: number | string = playerHeight;
    let calcFontSize: number | string = browserHeight > browserWidth ? 'x-small' : 'inherit';

    // NOTE: Check if browser height is really big or when the
    // browser winodw is narrow. Adjust the overlay to mat
    // the smaller player height and widths.
    const background = `url('/Harmonized_Shapes_Orange_Arctic_Gradient.svg') center center no-repeat #F9F8F5`;

    if (browserHeight > browserWidth) {
      calcFontSize = `2vw`;
    } else {
      calcFontSize = `3vw`;
    }

    this.overlayStyles = {
      position: 'absolute',
      top: `${topPosition}px`,
      left: `${leftPosition}px`,
      width: `${overlayWidth}px`,
      height: `${calcMaxHeight}px`,
      fontSize: calcFontSize,
      background: `${background}`,
    };
  }

  /**
   * Show congrats dialog when summary overlay is displayed
   */
  private showCongratsDialog(): void {
    const tierName = 'Producer';
    const slapFiveActivity = 'Watch Personalized Video';

    firstValueFrom(this.breakpointService.mobile$)
      .then((isMobile) => {
        this.dialog.open(CongratsDialogComponent, {
          width: isMobile ? '' : `${this.overlayStyles['width']}`,
          height: isMobile ? '' : `${this.overlayStyles['height']}`,
          maxWidth: BreakpointService.contentMaxWidth,
          autoFocus: false,
          backdropClass: 'transparent-backdrop',
          data: {
            title: `You completed: ${slapFiveActivity}`,
            body: `You're one step closer to achieving your next ${tierName} tier!`,
            hasAdvocacyPoints: true,
            dataAnalytics: ['onboarding', 'personalized welcome video', 'congrats'],
            points: 50,
            badgePath: 'images/program-images/general-activity-badge.svg',
            urlLink: `${environment.orchestrator.baseUrl}/profile/levels?#${tierName}`,
            urlLinkLabel: 'Review your progress',
          },
        });
      })
      .catch((error) => {
        console.error('Failed to open dialog:', error);
      });
  }
}
